import {
  Box,
  LinearProgress,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { BORDER_RADIUS, COLORS } from "app/theme";
import { useTranslation } from "common/locales";
import {
  useFarmerDashboards,
  useMetabaseUrl,
  useProgrammeActions,
} from "model";

const PlaceholderCard = styled(Box)({
  backgroundColor: "#FFFFFF",
  borderRadius: `${BORDER_RADIUS.medium}px`,
  border: "1px solid #e4e4e7",
  padding: "24px",
});

const ComingSoonBadge = styled(Box)({
  display: "inline-block",
  padding: "2px 8px",
  backgroundColor: COLORS.bellflower[100],
  borderRadius: `${BORDER_RADIUS.small}px`,
  marginLeft: "8px",
});

const DashboardContainer = styled(Box)({
  overflow: "hidden",
});

function ComingSoonPlaceholder() {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <PlaceholderCard>
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          alignItems: isMobile ? "flex-start" : "center",
          gap: isMobile ? 1 : 0,
          mb: 1,
        }}
      >
        <Typography variant="body2" fontWeight={600} sx={{ color: "#18181b" }}>
          {t("insights.assessments.title")}
        </Typography>
        <ComingSoonBadge sx={{ ml: isMobile ? 0 : 1 }}>
          <Typography
            variant="caption"
            sx={{ color: COLORS.bellflower[700], fontWeight: 500 }}
          >
            Coming Soon
          </Typography>
        </ComingSoonBadge>
      </Box>
      <Typography variant="body2" sx={{ color: "#71717a", fontSize: "13px" }}>
        {t("insights.assessments.description")}
      </Typography>
    </PlaceholderCard>
  );
}

export default function AssessmentsSection() {
  const { t } = useTranslation();
  const { data: programmes = [] } = useProgrammeActions();
  const {
    data: farmerDashboards = [],
    isLoading: dashboardsLoading,
    isError: dashboardsError,
  } = useFarmerDashboards();

  // Check if farmer is in any programme
  const isInProgramme = programmes.length > 0;

  // Get the first programme's dashboard (primary programme)
  const firstProgramme = programmes[0];
  const selectedDashboard = farmerDashboards.find(
    (d) => d.programmeId === firstProgramme?.guid
  );

  // Load Metabase URL
  const {
    data: metabaseData,
    isLoading: metabaseLoading,
    isError: metabaseError,
  } = useMetabaseUrl(
    selectedDashboard?.guid || null,
    {},
    !!selectedDashboard?.guid
  );
  const metabaseUrl = metabaseData?.url || "";

  // If farmer is not in a programme, show coming soon placeholder
  if (!isInProgramme) {
    return <ComingSoonPlaceholder />;
  }

  // Loading state for dashboards
  if (dashboardsLoading) {
    return (
      <DashboardContainer>
        <LinearProgress />
      </DashboardContainer>
    );
  }

  // Error loading dashboards
  if (dashboardsError) {
    return (
      <DashboardContainer
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "200px",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {t("insights.dashboardLoadError")}
        </Typography>
      </DashboardContainer>
    );
  }

  // No dashboard configured for this programme
  if (!selectedDashboard) {
    return (
      <DashboardContainer
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "200px",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {t("insights.noDashboard")}
        </Typography>
      </DashboardContainer>
    );
  }

  // Loading Metabase URL
  if (metabaseLoading) {
    return (
      <DashboardContainer>
        <LinearProgress />
      </DashboardContainer>
    );
  }

  // Error loading Metabase URL
  if (metabaseError || !metabaseUrl) {
    return (
      <DashboardContainer
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "200px",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {t("insights.dashboardLoadError")}
        </Typography>
      </DashboardContainer>
    );
  }

  // Show the programme dashboard
  return (
    <DashboardContainer>
      <iframe
        frameBorder="0"
        src={metabaseUrl}
        width="100%"
        height="2000px"
        title="Programme-Dashboard"
        style={{
          border: "none",
          display: "block",
          width: "100%",
        }}
      />
    </DashboardContainer>
  );
}
